import React, { useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, SearchBox, RefinementList } from "react-instantsearch";
import InfiniteHits from "./infinite_hits";
import LocationFilter from "../../source_directory/LocationFilter";
import SearchToolbar from "../../source_directory/SearchToolbar";
import FilterPill from "../../source_directory/FilterPill";
import FilterClearEffect from "../../source_directory/FilterClearEffect";
import sharedAlgoliaRouter from "../../source_directory/sharedAlgoliaRouter";
import useSavedOnlyFilter from "../../source_directory/useSavedOnlyFilter";
import DefaultIndexConfigure from "../../source_directory/DefaultIndexConfigure";
import withUnfilteredDefaultFacets from "../../source_directory/withUnfilteredDefaultFacets";
import useOwnedLocationParams from "../../source_directory/useOwnedLocationParams";
import useDebouncedQueryHook from "../../source_directory/useDebouncedQueryHook";

const OWN_TAB = "experts";

function ExpertsSearch({
  algoliaAppId,
  algoliaSearchKey,
  indexName,
  viewMode,
  showFilters,
  onToggleFilters,
  savedOnly,
  onToggleSavedOnly,
  onViewModeChange,
  groupCount,
  onMyGroupsClick,
  initialPathname = undefined,
}) {
  const queryHook = useDebouncedQueryHook();
  const searchClient = useMemo(
    () => withUnfilteredDefaultFacets(algoliasearch(algoliaAppId, algoliaSearchKey)),
    [algoliaAppId, algoliaSearchKey],
  );
  const savedFilter = useSavedOnlyFilter(savedOnly, "Id::PromoEntity::Expert");

  const getSearchParamsFromUrl = () => {
    const url = new URL(window.location.href);
    return {
      query: url.searchParams.get("query"),
      companyName: url.searchParams.get("company_name"),
      gender: url.searchParams.get("gender"),
      title: url.searchParams.get("title"),
      locationName: url.searchParams.get("location_name"),
      locationRadius: url.searchParams.get("location_radius"),
    };
  };

  const transformGenders = (inputString) => {
    const mappings = {
      female: "Female",
      male: "Male",
      nonbinary: "Non-binary +",
      notspecified: "Not specified",
    };
    return inputString
      .split(",")
      .map((gender) => mappings[gender])
      .filter(Boolean);
  };

  // Rewrite friendly deep-link params into Algolia's routing format synchronously during the first
  // render, before the child InstantSearch reads the URL on mount. A useEffect would run too late.
  const didTransformUrl = useRef(false);
  if (!didTransformUrl.current) {
    didTransformUrl.current = true;
    const currentUrl = new URL(window.location.href);
    const searchParams = getSearchParamsFromUrl();
    const paramsToTransform = ["query", "companyName", "gender", "title"];
    const needsTransformation = paramsToTransform.some((param) => searchParams[param] !== null);
    const hasAlgoliaParams = Array.from(currentUrl.searchParams.keys()).some((key) => key.includes(`${indexName}[`));

    if (needsTransformation && !hasAlgoliaParams) {
      const newUrl = new URL(window.location.href);
      // Drop only the consumed legacy params — anything else in the URL (saved=1, another
      // tab's filter state, location_name/location_radius) is not this transform's to discard.
      ["query", "company_name", "gender", "title"].forEach((param) => newUrl.searchParams.delete(param));
      if (searchParams.query) newUrl.searchParams.set(`${indexName}[query]`, searchParams.query);
      if (searchParams.companyName)
        newUrl.searchParams.set(`${indexName}[refinementList][jobs.company][0]`, searchParams.companyName);
      if (searchParams.gender) {
        const genders = transformGenders(searchParams.gender);
        genders.forEach((gender, index) => {
          newUrl.searchParams.set(`${indexName}[refinementList][gender][${index}]`, gender);
        });
      }
      if (searchParams.title)
        newUrl.searchParams.set(`${indexName}[refinementList][jobs.title][0]`, searchParams.title);
      window.history.replaceState(null, "", newUrl.toString());
    }
  }

  const { locationName, locationRadius } = useOwnedLocationParams(initialPathname, OWN_TAB);
  const [locationResetSignal, setLocationResetSignal] = useState(0);

  return (
    <div className="ais-InstantSearch">
      <InstantSearch
        indexName={indexName}
        searchClient={searchClient}
        routing={{ router: sharedAlgoliaRouter }}
        future={{ preserveSharedStateOnUnmount: true, cleanUrlOnDispose: false }}
      >
        <DefaultIndexConfigure baseFilter={savedFilter} unfilteredFacets={showFilters} />
        <FilterClearEffect showFilters={showFilters} onClear={() => setLocationResetSignal((signal) => signal + 1)} />
        <SearchToolbar
          showFilters={showFilters}
          onToggleFilters={onToggleFilters}
          savedOnly={savedOnly}
          onToggleSavedOnly={onToggleSavedOnly}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          groupCount={groupCount}
          onMyGroupsClick={onMyGroupsClick}
        >
          <SearchBox autoFocus placeholder="Search" searchAsYouType queryHook={queryHook} />
        </SearchToolbar>

        <div
          className="sd-filter-row"
          style={{ display: showFilters ? "flex" : "none", flexWrap: "wrap", gap: 8, marginBottom: 12 }}
        >
          <FilterPill label="Gender" attribute="gender">
            <RefinementList
              attribute="gender"
              limit={6}
              showMore={false}
              searchable={false}
              transformItems={(items) => [...items].sort((a, b) => a.label.localeCompare(b.label))}
            />
          </FilterPill>
          <FilterPill label="Company" attribute="jobs.company">
            <RefinementList attribute="jobs.company" limit={10} showMore searchable />
          </FilterPill>
          <FilterPill label="Title" attribute="jobs.title">
            <RefinementList attribute="jobs.title" limit={10} showMore searchable />
          </FilterPill>
          <LocationFilter
            initialLocationName={locationName}
            initialLocationRadius={locationRadius}
            resetSignal={locationResetSignal}
          />
        </div>

        <InfiniteHits viewMode={viewMode} />
      </InstantSearch>
    </div>
  );
}

ExpertsSearch.propTypes = {
  algoliaAppId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  indexName: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(["grid", "list"]).isRequired,
  showFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func.isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  groupCount: PropTypes.number,
  onMyGroupsClick: PropTypes.func,
  initialPathname: PropTypes.string,
};

export default ExpertsSearch;
